/**
 * client/_nav.ts
 *
 * The Client's header + side menu — a STATIC, hardcoded nav rather than the
 * dynamic per-org menu the 'user' shell resolves from `USER_DATA.menu` (see
 * user/_nav.ts). Per your call: every Client sees exactly the same three
 * pages today (Profile, Programs, Organizations), so there's nothing to
 * personalize per-Client yet — no menu-DB rows, no vd_menu_catalog entries,
 * no allowed_roles gating. If that changes later (different Clients seeing
 * different things), this is the one function to swap out for a real fetch
 * against the menu engine — every consumer of getClientNavData() stays the
 * same either way, since the RETURN SHAPE below is deliberately identical to
 * user/_nav.ts's getNavData(): { headerItems, navItems }.
 *
 * NOT included here: "Profile". Following the same pattern the 'user' shell
 * already uses — profile is never a menu item in _nav.ts's headerItems
 * either, it's the default redirect (`/user` → `/user/profile`) reached via
 * the avatar dropdown in the header, which is markup in user.component.html,
 * not data in _nav.ts. Client mirrors that exactly: `/client` → redirects to
 * `/client/profile`, and the avatar dropdown link is wired in the (still
 * needed) client.component.html the same way. See the note I've sent back
 * asking for user.component.ts/.html — once I have it I'll confirm the
 * avatar-dropdown assumption and wire it precisely instead of leaving it
 * implicit.
 *
 * ASSUMPTION FLAGGED: "Organizations" is a single page with no sub-items
 * (Clients don't create/edit Organizations directly — an org either gets
 * selected or invited-by-email at Program-creation time, per the spec). If
 * you want a fuller Organizations sub-menu later, add a `children` array
 * here the same way "Programs" has one below.
 */

export function getClientNavData(): { headerItems: any[]; navItems: any[] } {

  const headerItems: any[] = [
    {
      menu_level: 0,
      menu_id: 'client-programs',
      name: 'Programs',
      url: '/client/program',
      type: 0,
      content: 'Create and manage programs across your organizations',
      children: [
        {
          menu_level: 1,
          menu_id: 'client-programs-view',
          parent_menu: 'client-programs',
          name: 'View Programs',
          url: '/client/program/view',
          type: 0,
          content: 'All programs across every organization you are parent of',
        },
        {
          menu_level: 1,
          menu_id: 'client-programs-create',
          parent_menu: 'client-programs',
          name: 'Create Program',
          url: '/client/program/create',
          type: 0,
          content: 'Select an existing organization, or invite a new one by email',
        },
        {
          menu_level: 1,
          menu_id: 'client-programs-edit',
          parent_menu: 'client-programs',
          name: 'Edit Program',
          url: '/client/program/edit',
          type: 0,
          content: 'Only programs your Client account created can be edited',
        },
        {
          menu_level: 1,
          menu_id: 'client-programs-delete',
          parent_menu: 'client-programs',
          name: 'Delete Program',
          url: '/client/program/delete',
          type: 0,
          content: 'Only programs your Client account created can be deleted',
        },
      ],
    },
    {
      menu_level: 0,
      menu_id: 'client-organizations',
      name: 'Organizations',
      url: '/client/organizations',
      type: 0,
      content: 'All organizations registered under your account',
      children: [],
    },
  ];

  // Side nav mirrors the FIRST header item's children, matching user/_nav.ts's
  // convention (navItemsServer only ever gets the first header menu's
  // children pushed to it — see the `parseInt(mKey) === 0` check there).
  const navItems: any[] = [headerItems[0]];

  return { headerItems, navItems };
}
